import React from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { IconSystemCrossFilled } from '@moonpig/launchpad-assets'
import { spacingPx } from '@moonpig/launchpad-theme'

const ICON_SIZE = spacingPx(8)

interface ChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  dismissible?: boolean
  selected: boolean
}

const StyledIconCross = styled(IconSystemCrossFilled)`
  transition: color 100ms ease-in-out;
  width: ${ICON_SIZE};
  height: ${ICON_SIZE};
  ${s({
    ml: 4,
    color: 'colorInteractionSelectedState',
  })}
`

const StyledChip = styled.button<Pick<ChipProps, 'selected'>>`
  transition: background-color 100ms ease-in-out, border-color 100ms ease-in-out,
    color 100ms ease-in-out;
  ${s({
    border: 0,
    paddingY: '10px',
    paddingX: '12px',
    borderRadius: 6,
    typography: 'typeBodyLabel',
  })}

  ${({ selected }) =>
    s({
      bgcolor: selected ? 'colorBackgroundSelectedState' : 'colorBlack05',
      color: selected ? 'colorInteractionSelectedState' : 'colorTextLabel',
    })}

  &:not(:disabled) {
    &:hover {
      ${s({
        border: 0,
        paddingY: '10px',
        paddingX: '12px',
        bgcolor: 'colorBlack10',
        typography: 'typeBodyLabel',
      })}

      ${({ selected }) =>
        s({
          color: selected
            ? 'colorInteractionSelectedStateHover'
            : 'colorTextLabel',
          bgcolor: selected ? 'colorBackgroundSelectedState' : 'colorBlack10',
        })}

      .dismiss-icon {
        ${({ selected }) =>
          s({
            color: selected
              ? 'colorInteractionSelectedStateHover'
              : 'colorInteractionSelectedState',
          })}
      }
    }

    &:focus-visible {
      text-decoration: none;
      outline: none;

      ${s({
        boxShadow: 5,
        border: 0,
        paddingY: '10px',
        paddingX: '12px',
        bgcolor: 'colorBlack10',
        typography: 'typeBodyLabel',
      })}

      ${({ selected }) =>
        s({
          color: selected
            ? 'colorInteractionSelectedStateHover'
            : 'colorTextLabel',
          bgcolor: selected ? 'colorBackgroundSelectedState' : 'colorBlack10',
        })}

      .dismiss-icon {
        ${({ selected }) =>
          s({
            color: selected
              ? 'colorInteractionSelectedStateHover'
              : 'colorInteractionSelectedState',
          })}
      }
    }
  }
`

const StyledSpan = styled.span`
  display: flex;
  text-align: center;
`

export const Chip: React.FC<React.PropsWithChildren<ChipProps>> = ({
  children,
  dismissible,
  selected,
  ...rest
}) => {
  const showDismissButton = selected && dismissible
  return (
    <StyledChip selected={selected} {...rest}>
      <StyledSpan>
        {children}
        {showDismissButton && <StyledIconCross className="dismiss-icon" />}
      </StyledSpan>
    </StyledChip>
  )
}
